import { useEffect, useRef, useState } from "react";
import { Mic, Pause, Play } from "lucide-react";
import {
  CHAT_VOICE_WAVE_BARS,
  drawVoiceWaveform,
  formatVoiceClock,
  loadVoicePeaks,
} from "@/lib/chatVoice";

type Props = {
  url: string;
  mime?: string | null;
  mine?: boolean;
  avatarUrl?: string | null;
  avatarLabel?: string;
  onAvatarClick?: () => void;
};

let activeStop: (() => void) | null = null;

export function ChatVoicePlayer({
  url,
  mime,
  mine,
  avatarUrl,
  avatarLabel,
  onAvatarClick,
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const peaksRef = useRef<number[]>(Array.from({ length: CHAT_VOICE_WAVE_BARS }, () => 0.22));
  const progressRef = useRef(0);
  const rafRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);

  const paint = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const styles = getComputedStyle(canvas);
    drawVoiceWaveform(ctx, canvas, peaksRef.current, {
      progress: progressRef.current,
      played: styles.getPropertyValue("--voice-played"),
      rest: styles.getPropertyValue("--voice-rest"),
    });
  };

  useEffect(() => {
    let alive = true;
    void loadVoicePeaks(url).then((peaks) => {
      if (!alive) return;
      peaksRef.current = peaks;
      paint();
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    const el = new Audio();
    el.preload = "metadata";
    if (mime) {
      try {
        el.setAttribute("type", mime);
      } catch {
        /* ignore */
      }
    }
    el.src = url;
    audioRef.current = el;

    const syncFromEl = (clock: boolean) => {
      const d = el.duration;
      const t = el.currentTime;
      if (Number.isFinite(d) && d > 0) {
        progressRef.current = Math.min(1, t / d);
        if (clock) setDuration(d * 1000);
      }
      if (clock) setElapsed(t * 1000);
      paint();
    };

    const onMeta = () => syncFromEl(true);
    const onEnded = () => {
      setPlaying(false);
      progressRef.current = 0;
      setElapsed(0);
      paint();
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("durationchange", onMeta);
    el.addEventListener("ended", onEnded);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);

    const onResize = () => paint();
    window.addEventListener("resize", onResize);
    paint();

    return () => {
      el.pause();
      el.removeAttribute("src");
      el.load();
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("durationchange", onMeta);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      window.removeEventListener("resize", onResize);
      if (activeStop && audioRef.current === el) activeStop = null;
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, mime]);

  useEffect(() => {
    if (!playing) {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      paint();
      return;
    }
    let lastClock = 0;
    const tick = (now: number) => {
      const el = audioRef.current;
      if (el && Number.isFinite(el.duration) && el.duration > 0) {
        progressRef.current = Math.min(1, el.currentTime / el.duration);
        if (now - lastClock > 200) {
          lastClock = now;
          setElapsed(el.currentTime * 1000);
          setDuration(el.duration * 1000);
        }
      }
      paint();
      rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, mine]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (!el.paused) {
      el.pause();
      return;
    }
    if (activeStop && activeStop !== stopSelf) activeStop();
    activeStop = stopSelf;
    void el.play().catch(() => undefined);
  };

  const stopSelf = () => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    el.currentTime = 0;
    setPlaying(false);
    progressRef.current = 0;
    setElapsed(0);
    paint();
  };

  const seek = (clientX: number) => {
    const el = audioRef.current;
    const canvas = canvasRef.current;
    if (!el || !canvas || !Number.isFinite(el.duration) || el.duration <= 0) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    el.currentTime = x * el.duration;
    progressRef.current = x;
    setElapsed(el.currentTime * 1000);
    paint();
  };

  const shown = playing ? elapsed : duration > 0 ? duration : elapsed;
  const initial = (avatarLabel || "?").trim().slice(0, 1).toUpperCase() || "?";
  const faceInner = (
    <>
      <span className="chat-voice-player__photo">
        {avatarUrl ? <img src={avatarUrl} alt="" /> : initial}
      </span>
      <span className="chat-voice-player__mic" aria-hidden>
        <Mic />
      </span>
    </>
  );

  return (
    <div className={`chat-voice-player ${mine ? "chat-voice-player--mine" : ""}`}>
      <button
        type="button"
        className="chat-voice-player__play"
        aria-label={playing ? "Pause" : "Lire le message vocal"}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
      >
        {playing ? <Pause className="size-3.5" fill="currentColor" /> : <Play className="size-3.5" fill="currentColor" />}
      </button>
      <canvas
        ref={canvasRef}
        className="chat-voice-player__wave"
        width={240}
        height={28}
        aria-hidden
        onClick={(e) => {
          e.stopPropagation();
          seek(e.clientX);
        }}
      />
      <span className="chat-voice-player__time">{formatVoiceClock(shown)}</span>
      {onAvatarClick ? (
        <button
          type="button"
          className="chat-voice-player__face"
          aria-label={`Photo de ${avatarLabel || "profil"}`}
          onClick={(e) => {
            e.stopPropagation();
            onAvatarClick();
          }}
        >
          {faceInner}
        </button>
      ) : (
        <span className="chat-voice-player__face">{faceInner}</span>
      )}
    </div>
  );
}
